import { isAndroidKiosk } from './androidBridge';

/**
 * The sign-in screen's "Remember me" choice, kept on this device.
 *
 * Two things hang off it. The server uses the flag to decide how long the
 * sign-in lasts (30 days, or until the browser/app closes and a few hours at
 * most). And the email address is kept and put back in the form next time, so a
 * person only types a password. The password is never stored here.
 *
 * Unticking forgets the address as well, so a shared computer is left as it was
 * found.
 */

const KEY = 'kortex.rememberMe';

interface Stored {
  remember: boolean;
  email: string;
}

export interface RememberedSignIn {
  remember: boolean;
  email: string;
}

/**
 * Ticked by default in the Android app, unticked in a browser. A till is meant
 * to stay signed in through the day and across restarts; a browser is as likely
 * to be somebody else's computer.
 */
const defaultRemember = (): boolean => isAndroidKiosk();

export function readRememberedSignIn(): RememberedSignIn {
  try {
    const stored = JSON.parse(window.localStorage.getItem(KEY) ?? 'null') as Partial<Stored> | null;
    if (stored && typeof stored.remember === 'boolean') {
      return {
        remember: stored.remember,
        email: stored.remember && typeof stored.email === 'string' ? stored.email : '',
      };
    }
  } catch {
    // Storage blocked or the value is junk: behave as a first visit.
  }
  return { remember: defaultRemember(), email: '' };
}

/** Called after a successful sign-in, with what was used. */
export function saveRememberedSignIn(remember: boolean, email: string): void {
  try {
    const value: Stored = { remember, email: remember ? email : '' };
    window.localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Private mode / storage full: the sign-in itself has already worked.
  }
}
